<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    default: null,
  },
  protocol: {
    type: String,
    default: '',
  },
  protocols: {
    type: Array,
    default: () => [],
  },
});
const emit = defineEmits(['viewFullHistory']);

const { t } = useI18n();
const store = useStore();
const isGeneratingProtocol = ref(false);

const currentProtocol = computed(() => props.protocol || '');
const protocolHistory = computed(() =>
  [...props.protocols]
    .filter(item => item?.protocol)
    .sort(
      (a, b) => new Date(b.generated_at || 0) - new Date(a.generated_at || 0)
    )
);
const visibleProtocolHistory = computed(() =>
  protocolHistory.value.slice(0, 3)
);

const generatedBy = item => {
  if (item.generated_by_name) return item.generated_by_name;
  if (item.source === 'resolution') {
    return t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.SOURCE_RESOLUTION');
  }
  return t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.SOURCE_SYSTEM');
};

const formattedDate = item => {
  if (!item.generated_at) return '';
  return new Intl.DateTimeFormat(undefined, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(item.generated_at));
};

const protocolDetails = item =>
  [formattedDate(item), generatedBy(item)].filter(Boolean).join(' · ');

const generateProtocol = async () => {
  if (!props.conversationId) return;

  isGeneratingProtocol.value = true;
  const generatedProtocol = await store.dispatch(
    'generateAttendanceProtocol',
    props.conversationId
  );
  isGeneratingProtocol.value = false;

  useAlert(
    generatedProtocol
      ? t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.GENERATED')
      : t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.ERROR')
  );
};

const copyProtocol = async protocol => {
  if (!protocol) return;

  await copyTextToClipboard(protocol);
  useAlert(t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.COPIED'));
};
</script>

<template>
  <section class="space-y-3 px-4 py-4">
    <div
      class="overflow-hidden rounded-xl border border-n-weak bg-n-alpha-2 shadow-sm"
    >
      <div class="border-b border-n-weak px-4 py-4">
        <div class="flex items-start justify-between gap-3">
          <div class="flex min-w-0 items-start gap-3">
            <span
              class="mt-0.5 inline-flex size-10 shrink-0 items-center justify-center rounded-lg border border-n-brand/30 bg-n-brand/10 text-n-brand"
            >
              <span class="i-lucide-fingerprint size-5" />
            </span>
            <div class="min-w-0">
              <p class="text-sm font-semibold text-n-slate-12">
                {{ t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.TITLE') }}
              </p>
              <p class="mt-1 text-xs leading-4 text-n-slate-10">
                {{
                  currentProtocol
                    ? t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.DESCRIPTION_READY')
                    : t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.DESCRIPTION_PENDING')
                }}
              </p>
            </div>
          </div>
          <span
            class="inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-1 text-xs font-medium"
            :class="
              currentProtocol
                ? 'border-n-teal-9/30 bg-n-teal-3 text-n-teal-11'
                : 'border-n-amber-9/30 bg-n-amber-3 text-n-amber-11'
            "
          >
            <span
              :class="
                currentProtocol ? 'i-lucide-check-circle-2' : 'i-lucide-clock-3'
              "
              class="size-3.5"
            />
            {{
              currentProtocol
                ? t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.STATUS_READY')
                : t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.STATUS_PENDING')
            }}
          </span>
        </div>
      </div>

      <div class="space-y-3 px-4 py-4">
        <div
          class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-3"
          :class="currentProtocol ? 'text-n-slate-12' : 'text-n-slate-10'"
        >
          <p class="mb-1 text-[11px] font-medium uppercase text-n-slate-9">
            {{ t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.CODE_LABEL') }}
          </p>
          <p class="break-all font-mono text-base font-semibold leading-6">
            {{
              currentProtocol || t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.EMPTY')
            }}
          </p>
        </div>

        <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <button
            v-if="currentProtocol"
            type="button"
            class="inline-flex items-center justify-center gap-2 rounded-lg border border-n-weak px-3 py-2.5 text-sm font-semibold text-n-slate-12 transition hover:border-n-brand hover:text-n-brand"
            @click="copyProtocol(currentProtocol)"
          >
            <span class="i-lucide-copy size-4" />
            {{ t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.COPY') }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center gap-2 rounded-lg border border-n-brand bg-n-brand px-3 py-2.5 text-sm font-semibold text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
            :disabled="isGeneratingProtocol"
            @click="generateProtocol"
          >
            <span
              :class="
                isGeneratingProtocol
                  ? 'i-lucide-loader-2 animate-spin'
                  : 'i-lucide-hash'
              "
              class="size-4"
            />
            {{
              currentProtocol
                ? t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.GENERATE_NEW')
                : t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.GENERATE')
            }}
          </button>
        </div>
      </div>
    </div>

    <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-3">
      <div class="mb-3 flex items-center justify-between gap-2">
        <p class="text-sm font-semibold text-n-slate-12">
          {{ t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.HISTORY_TITLE') }}
        </p>
        <span class="text-xs text-n-slate-10">
          {{ protocolHistory.length }}
        </span>
      </div>

      <div v-if="protocolHistory.length" class="space-y-2">
        <div
          v-for="item in visibleProtocolHistory"
          :key="`${item.protocol}-${item.generated_at}`"
          class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2"
        >
          <div class="flex items-start justify-between gap-2">
            <span
              class="break-all font-mono text-sm font-semibold text-n-slate-12"
            >
              {{ item.protocol }}
            </span>
            <button
              type="button"
              class="shrink-0 text-n-slate-10 transition hover:text-n-brand"
              @click="copyProtocol(item.protocol)"
            >
              <span class="i-lucide-copy size-4" />
            </button>
          </div>
          <p class="mt-1 text-xs text-n-slate-10">
            {{ protocolDetails(item) }}
          </p>
        </div>
        <button
          v-if="protocolHistory.length > visibleProtocolHistory.length"
          type="button"
          class="inline-flex w-full items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-n-brand transition hover:bg-n-alpha-2"
          @click="emit('viewFullHistory')"
        >
          {{ t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.VIEW_FULL_HISTORY') }}
          <span class="i-lucide-arrow-up-right size-4" />
        </button>
      </div>
      <p v-else class="text-sm text-n-slate-10">
        {{ t('CONTACT_PANEL.ATTENDANCE_PROTOCOL.HISTORY_EMPTY') }}
      </p>
    </div>
  </section>
</template>
